import { COMMA, ENTER, TAB } from '@angular/cdk/keycodes';
import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipGrid, MatChipInputEvent } from '@angular/material/chips';
import { defer, from, Observable, of } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    map,
    startWith,
    switchMap
} from 'rxjs/operators';
import { SortDirection, SortType } from '../../constants-types';
import {
    FilterInfo,
    FilterType,
    SortInfo,
    UserStub,
    UserStubWithState
} from '../../core-data-model';
import { UserApi } from '../../core-services';
import { IUserManagementService } from '../../core-services/UserManagement/user-management.service';
import { StringUtilities } from '../../core-utilities';
import { UserFactory } from '../../object-factory';
import {
    FeatureFlags,
    FeatureFlagsService,
    InitDataService
} from '../../system-services';
import { TranslationService } from '../../translations';
import { EmailDomainValidatorDirective } from '../Directives';

/**
 * @title User Typeahead
 */
@Component({
    selector: 'pi-user-typeahead',
    templateUrl: './user-typeahead.component.html',
    styleUrls: ['./user-typeahead.component.less'],
    standalone: false
})
export class UserTypeaheadComponent {
    @Input()
    maxNumItems = -1;
    @Input()
    disabled: boolean;
    // need a generic filter service
    @Input()
    filterService: IUserManagementService;

    @ViewChild('formInput') formInput: ElementRef<HTMLInputElement>;
    @ViewChild('chipList') chipList: MatChipGrid;

    separatorKeysCodes: number[] = [ENTER, COMMA, TAB];
    formControl = new UntypedFormControl();
    typeaheadList: Observable<UserStubWithState[]>;
    selectedUsers: UserStubWithState[] = [];

    isLocal = false;
    seededItems: Observable<UserStubWithState[]> = of([]);
    errorStateMessages: string[];
    invalidEmails: string[] = [];

    constructor(
        private initDataService: InitDataService,
        private stringUtility: StringUtilities,
        private userFactory: UserFactory,
        private userApi: UserApi,
        public TR: TranslationService,
        private featureFlagService: FeatureFlagsService
    ) {
        this.typeaheadList = this.formControl.valueChanges.pipe(
            startWith(''),
            debounceTime(400),
            distinctUntilChanged(),
            switchMap((item) => this._filter(item || ''))
        );
    }

    // from input in mat-chip-list
    inputTokenEnd(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();
        this.tryadd({ email: value });
        this.inputReset();
    }

    // from mat-autocomplete
    inputAutocompleteSelected(event: MatAutocompleteSelectedEvent): void {
        this.tryadd(event.option.value);
        this.inputReset();
    }

    // clear input value
    inputReset() {
        this.formInput.nativeElement.value = '';
        this.formControl.setValue(null);
    }

    paste(event: ClipboardEvent): void {
        event.preventDefault();
        const data = event.clipboardData.getData('Text').split(/;|,|\n/);
        if (data.length <= 1) {
            this.formInput.nativeElement.value += data;
            return;
        }
        data.forEach((value) => {
            const v = value.trim();
            if (v.length > 0) {
                this.tryadd({ email: value.trim() });
            }
        });
    }

    tryadd(userStubItem: Partial<UserStubWithState>): boolean {
        // error on maximum number of items
        const email = userStubItem.email;
        if (this.hasMaxNumberItems()) {
            this.setError(this.TR.key('userManagement.maximumUsers.error'));
            return false;
        }

        userStubItem.stateInvalid = this.isInvalidEmail(email);
        // if value does not exist in input list
        // if value does not have prefix
        const userStub = this.userFactory.new_UserToStubWithState(userStubItem);
        if (email && !this.doesUserAlreadyExist(userStub)) {
            this.selectedUsers.push(userStub);
            return true;
        }
        this.setError(
            this.TR.key('userManagement.userAlreadyExists.error'),
            false
        );
        return false;
    }

    remove(item: string): void {
        const index = this.selectedUsers.map((x) => x.email).indexOf(item);

        if (index >= 0) {
            this.selectedUsers.splice(index, 1);
        }

        // error state on max number of items
        this.chipList.errorState = this.hasMaxNumberItems();
    }

    getCompanyEmailDomains() {
        if (
            this.featureFlagService.getByFlag(
                FeatureFlags.enableCompanyEmailDomainIndex
            )
        ) {
            return this.initDataService.companyEmailDomains.allowList;
        }
        return this.filterService.allowedCompanyDomains;
    }

    isInvalidEmail(email: string): boolean {
        return (
            !this.stringUtility.isAnEmailString(email) ||
            !this.getCompanyEmailDomains().includes(
                EmailDomainValidatorDirective.extractDomain(email)
            )
        );
    }

    invalidEmailError() {
        this.invalidEmails = this.selectedUsers
            .filter((x) => this.isInvalidEmail(x.email))
            .map((x) => x.email);

        this.setError(
            this.stringUtility.formatString(
                this.TR.key('userTypeahead-invalidEmail.error'),
                this.getCompanyEmailDomains().join(', ')
            )
        );
    }

    displayText(user: UserStubWithState): string {
        if (this.isInvalidEmail(user.email)) {
            this.invalidEmailError();
        }

        return user.firstName
            ? this.stringUtility.displayName(user.firstName, user.lastName)
            : user.email;
    }

    hasMaxNumberItems(): boolean {
        return (
            this.maxNumItems > -1 &&
            this.selectedUsers.length >= this.maxNumItems
        );
    }

    doesUserAlreadyExist(user: UserStub): boolean {
        return (
            !this.selectedUsers.every(
                (x) => x.email.toLowerCase() != user.email.toLowerCase()
            ) ||
            !this.filterService ||
            !this.filterService
                .getExistingUsers()
                .every(
                    (x) => x.email.toLowerCase() !== user.email.toLowerCase()
                )
        );
    }

    setError(errorMessage: string, toggleErrorState = true) {
        if (toggleErrorState) {
            this.chipList.errorState = true;
        }
        this.errorStateMessages = [errorMessage];
    }
    addError(errorMessage: string) {
        this.chipList.errorState = true;
        this.errorStateMessages.push(errorMessage);
    }
    setInvalidChips(email: string[]) {
        this.selectedUsers.forEach((u) => {
            if (email.includes(u.email)) {
                u.stateInvalid = true;
            }
        });
    }
    validation() {
        return this.chipList.errorState;
    }

    invitationTextHint(): string {
        return this.stringUtility.formatString(
            this.TR.key('userManagement.hintText'),
            this.maxNumItems - this.selectedUsers.length
        );
    }

    private _filter(value: string): Observable<UserStubWithState[]> {
        const filterValue = value ? value.toLowerCase() : '';
        if (filterValue.length <= 2) {
            return of([]);
        }

        // this is where you do simple contains search with filterValue on current active list
        // then add result here
        const existingEmails = this.selectedUsers
            .filter(
                (users) =>
                    users.firstName != null &&
                    (users.firstName.includes(filterValue) ||
                        users.lastName.includes(filterValue) ||
                        users.email.includes(filterValue))
            )
            .map((x) => encodeURIComponent(x.email));
        // if supplied a local list, do not search userAPI.
        if (this.isLocal) {
            return this.localSearchForUsers(filterValue);
        }

        // else search via UserAPI
        return defer(() =>
            from(this.userApiSearchForUsers(filterValue, existingEmails))
        );
    }

    private localSearchForUsers(
        filterText: string
    ): Observable<UserStubWithState[]> {
        return this.seededItems.pipe(
            map((response) =>
                response.filter((item) =>
                    item.getFullDisplayName().toLowerCase().includes(filterText)
                )
            )
        );
    }

    private userApiSearchForUsers(
        filterText: string,
        omitEmails: string[]
    ): Promise<UserStubWithState[]> {
        const filters = [
            new FilterInfo({
                type: FilterType.Search,
                parameters: { term: filterText }
            })
        ];
        if (omitEmails && omitEmails.length > 0) {
            filters.push(
                new FilterInfo({
                    type: FilterType.NotInEmail,
                    parameters: omitEmails
                })
            );
        }
        const sort = [
            new SortInfo(SortType.Name, SortDirection.Ascending),
            new SortInfo(SortType.Email, SortDirection.Ascending)
        ];
        return this.userApi
            .getUsers(5, filters, 'all', sort)
            .then((users) =>
                users.map((u) => this.userFactory.new_UserToStubWithState(u))
            );
    }
}
